/* eslint-disable max-lines */
/* eslint-disable max-statements */
import { useCallback, useEffect, useState } from "react";
import { LoaderFunctionArgs, redirect, useFetcher, useLoaderData, useParams } from "react-router-dom";
import { packObjForSubmit, unpackObjForSubmit } from "../../utils";
import { doFetch } from "../../utils/doFetch";
import { doFetchPut } from "../../utils/doFetchPut";
import { groupCandidatesByBoard } from "../../utils/groupCandidatesByBoard";
import { DoaForecastFilterBoardType, SortType } from "../doa-forecast-stats/DoaForecastStatsModels";
import { DoaForecastStats } from "./DoaForecastStats";
import { DetailsItem, DetailsItemType, IAvailableList, IDoaForecastStatsData, IDoaForecastStatsDispatch, IShortageBoardOptions, IShortageCandidateInfo, IShortageList, IShortageListFilter, IShortageListItem, IShortageListRawData } from "./DoaForecastStatsData";
import { DoaForecastList, StatusType } from "./DoaForecastStatsModels";

export const doaForecastDoaAdminStatsRouteLoader = async ({ request, params }: LoaderFunctionArgs) => {
  const { dispatch, date, jobId, flag545, list, boardType } = params;
  let dispatchData: IDoaForecastStatsData | null = null;
  let availabilityList: IAvailableList[] | null = null;
  let shortagesList: IShortageList[] | null = null;
  try {
    if (dispatch && date&& !list) {
      dispatchData = await doFetch(`doa-forecast/doa-admin/${dispatch}/${date}/stats`, { signal: request.signal }, true, true);
    }
    if (dispatch && date && jobId && flag545 && list) {
      const isShortageList = list === DoaForecastList.shortage;
      const result = await doFetch(`doa-forecast/doa-admin/${dispatch}/${date}/${jobId}/${flag545}/${boardType ? `${boardType}/` : ''}${list}`, {}, true, true);
      if (result && Object.keys(result).length > 0) {
        isShortageList ? (shortagesList = result) : (availabilityList = result);
      }
    }
  } catch (err) {
    return redirect(`/doa-forecast`);
  }
  return {
    dispatchData,
    availabilityList,
    shortagesList,
  };
}

export const doaForecastDoaAdminStatsRouteAction = async ({ request, params }: { request: any, params: any }) => {
  let formData = await request.formData();
  let obj = JSON.parse(unpackObjForSubmit(formData));
  try {
    if (params.dispatch && params.date && params.regId && params.doaSelection) {
      const res = await doFetchPut(
        `doa-forecast/doa-admin/${params.dispatch}/${params.date}/${obj.jobId}/${params.regId}/${params.doaSelection}`,
        { signal: request.signal },
      );

      return {
        ...obj,
        ...!res.success && ({ errorMsg: res.message })
      }
    } else if (params.dispatch && params.date) {
      const res = await doFetchPut(
        `doa-forecast/doa-admin/${params.dispatch}/${params.date}/shortage-list/contacted`,
        { signal: request.signal },
        { regId: obj.regId, contacted: obj.contacted }
      );

      if (res.success) return obj;
      else {
        // if result is not success, do not update
        return { ...obj, contacted: !obj.contacted };
      }
    }
  } catch (error: any) {
    console.error(error);
    throw error;
  }
  return obj;
}

const defaultFilter = {
  selectedBoardType: DoaForecastFilterBoardType.union,
  selectedBoardFilter: '',
  selectedSortType: SortType.buttonPosition,
  selectedNameFilter: '',
  includeCallback: false,
};

export const DoaForecastDoaAdminStatsRoute = () => {
  const fetcher = useFetcher();
  const loaderData = useLoaderData() as IDoaForecastStatsData;
  const params = useParams();
  let data = fetcher.data ?? loaderData;
  const [availableLists, setAvailableLists] = useState<IAvailableList[]>([]);
  // need to keep a copy of unfiltered shortage lists returned from the backend for filtering
  // once the doa admin makes someone available, that person gets removed from the list
  // this copy in the frontend helps us to keep track of the original shortage list so that admin can make users unavailable
  const [shortageListRawData, setShortageListRawData] = useState<IShortageListRawData | null>(data.shortagesList);
  const [dispatchData, setDispatchData] = useState<IDoaForecastStatsDispatch | null>(data.dispatchData);
  const [filteredShortageLists, setFilteredShortageLists] = useState<IShortageList[]>([]);
  const [filter, setFilter] = useState<IShortageListFilter>(defaultFilter);
  const [isFulfilled, setIsFulfilled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);

  const [jobInfo, setJobInfo] = useState<{
    jobId: number | null,
    flag545: number | null
  }>({ jobId: null, flag545: null });

  // effect for setting loading and submitting states
  useEffect(() => {
    setLoading(fetcher.state === 'loading');
    setSubmitting(fetcher.state === 'submitting');
  }, [fetcher.state])

  // effect for setting dispatch data and shortage lists
  useEffect(() => {
    if (data.dispatchData) setDispatchData(data.dispatchData);
    if (data.shortagesList) setShortageListRawData(data.shortagesList);
  }, [data])

  const filterAndSortList = useCallback((
    jobId: number,
    flag545: number,
    {
      boardData,
      boardOptions,
      candidateInfo
    }: {
      boardData: IShortageListItem[],
      boardOptions: IShortageBoardOptions[],
      candidateInfo: IShortageCandidateInfo[]
    }
  ) => {
    let filteredCandidates: IShortageCandidateInfo[] = candidateInfo;
    // filter by board
    if (filter.selectedBoardFilter) {
      filteredCandidates = filteredCandidates.filter(d => d.boardCode === filter.selectedBoardFilter);
    }
    // filter by employee name
    if (filter.selectedNameFilter) {
      const filterByName = (c: IShortageCandidateInfo) =>
        c.employeeLastName?.includes(filter.selectedNameFilter.toUpperCase());

      filteredCandidates = filteredCandidates.filter(c => filterByName(c));
    }
    // filter by include callback
    if (!filter.includeCallback) {
      const filterByNotCallback = (c: IShortageCandidateInfo) => !c.isCallback
      filteredCandidates = filteredCandidates.filter(c => filterByNotCallback(c));
    }
    // sort by buttonPosition: sortButtonPosition (default)
    // sort by boardPlate: sortCandidatePosition
    // sort by name: employeeLastName
    if (filter.selectedSortType === SortType.buttonPosition) {
      filteredCandidates.sort((a, b) => a.sortButtonPosition - b.sortButtonPosition);
    } else if (filter.selectedSortType === SortType.boardPlate) {
      filteredCandidates.sort((a, b) => a.sortCandidatePosition - b.sortCandidatePosition);
    } else if (filter.selectedSortType === SortType.name) {
      filteredCandidates.sort((a, b) => (a.employeeLastName || '').localeCompare(b.employeeLastName || ''));
    }
    let groupedBoardData: IShortageListItem[] = [];
    if (filter.selectedSortType === SortType.name) {
      const signleGroup = {
        sortBoardPosition: 0,
        boardCode: "",
        boardDescription: "",
        isCasual: false,
        buttonImage: null,
        buttonDescription: null,
        plateNumber: null,
        candidateInfo: filteredCandidates,
        buttonInfo: []
      }

      groupedBoardData = [signleGroup];
    } else {
      groupedBoardData = groupCandidatesByBoard(filteredCandidates, boardData);
    }

    setFilteredShortageLists([{
      list: groupedBoardData,
      boardOptions,
      jobId,
      flag545
    }]);
  }, [filter]);

  // effect to trigger filter and sort on raw data change
  useEffect(() => {
    const { jobId, flag545 } = jobInfo;
    if (jobId != null && flag545 != null && shortageListRawData) {
      filterAndSortList(
        jobId,
        flag545,
        {
          boardData: shortageListRawData.boardData,
          boardOptions: shortageListRawData.boardOptions,
          candidateInfo: shortageListRawData.candidateInfo
        }
      );
    }
  }, [jobInfo, filterAndSortList, shortageListRawData])

  const loadShortageList = useCallback(async (
    list: DoaForecastList,
    jobId: number,
    flag545: number,
    boardType: string
  ) => {
    try {
      setLoading(true);
      fetcher.load(`/doa-forecast/doa-admin/${params.dispatch}/${params.date}/${jobId}/${flag545}/${boardType}/${list}/stats`);
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }, [fetcher, params])

  const loadAvailableList = async (list: DoaForecastList, jobId: number, flag545: number) => {
    try {
      setLoading(true);
      fetcher.load(`/doa-forecast/doa-admin/${params.dispatch}/${params.date}/${jobId}/${flag545}/${list}/stats`);
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  }

  // effect to render updated data
  useEffect(() => {
    if (fetcher?.data?.updateOnly) {
      const { regId } = fetcher.data;
      const hasError = fetcher.data.errorMsg;
      const hasContacted = fetcher.data.hasOwnProperty('contacted');
      const hasIsAvailable = fetcher.data.hasOwnProperty('isAvailable');
      const adjustment = fetcher.data.isAvailable ? 1 : -1;

      // update states
      const updateCandidateInfo = (candidateInfo: IShortageCandidateInfo[]) => {
        return candidateInfo.map(c => {
          if (c.registrationNumber === regId) {
            if (hasError) {
              return { ...c, errorMsg: fetcher.data.errorMsg }
            }
            if (hasContacted) {
              return { ...c, hasBeenContacted: !c.hasBeenContacted }
            }
            if (hasIsAvailable) {
              // when a candidate is made avaiable, need to also set contacted to true
              const isAvailable = fetcher.data.isAvailable;
              return {
                ...c,
                isAvailable,
                hasBeenContacted: isAvailable ? true : c.hasBeenContacted
              };
            }
          }
          return c;
        });
      }

      // update dispatch stat
      const updateDispatchDataDetails = (details: DetailsItem[]) => {
        let type = DetailsItemType.regular;
        if (dispatchData?.dispatch === "1745") {
          type = tab ? DetailsItemType.coastwise : DetailsItemType.jobs;
        }
        return details.map(d => {
          if (d.type === type) {
            return {
              ...d,
              jobStats: d.jobStats.map(j => {
                if (j.jobId === jobInfo.jobId && j.flag545 === jobInfo.flag545) {
                  if (j.short + adjustment === 0) setIsFulfilled(true);
                  else setIsFulfilled(false);
                  return {
                    ...j,
                    totalAvailable: j.totalAvailable + adjustment,
                    matchedAvailable: j.matchedAvailable + adjustment,
                    short: j.short + adjustment,
                  }
                }
                return j;
              })
            }
          }
          return d;
        });
      };

      if (shortageListRawData) {
        const updatedShortageListRawData = {
          ...shortageListRawData,
          candidateInfo: updateCandidateInfo(shortageListRawData.candidateInfo)
        }

        // update raw data after any actions
        setShortageListRawData(updatedShortageListRawData);
      }
      // update dispatch stats
      if (dispatchData && !hasError && hasIsAvailable) {
        const updatedDispatchData = {
          ...dispatchData,
          totalEmployees: dispatchData.totalEmployees + adjustment,
          details: updateDispatchDataDetails(dispatchData.details)
        }

        setDispatchData(updatedDispatchData);
      }
    } else if (fetcher.data?.availabilityList) {
      setAvailableLists(prev => [
        ...prev,
        {
          list: fetcher.data.availabilityList.list.length ? fetcher.data.availabilityList.list : [],
          jobId: fetcher.data.availabilityList.jobId,
          flag545: fetcher.data.availabilityList.flag545
        }
      ]);
    } else if (fetcher.data?.shortagesList) {
      filterAndSortList(
        fetcher.data.shortagesList.jobId,
        fetcher.data.shortagesList.flag545,
        fetcher.data.shortagesList
      );
    }
  }, [fetcher.data]);

  const onRefresh = () => {
    fetcher.load(`/doa-forecast/doa-admin/${params.dispatch}/${params.date}/stats`);
    setAvailableLists([]);
    setFilteredShortageLists([]);
  }

  const onUpdateCandidateStatuses = (
    jobId: number,
    flag545: number,
    boardCode: string,
    regId: number,
    statusType: StatusType,
    status: boolean
  ) => {
    // submit action
    const formData = packObjForSubmit({
      updateOnly: true,
      jobId,
      flag545,
      boardCode,
      regId,
      ...(statusType === StatusType.contacted && { contacted: status }),
      ...(statusType === StatusType.available && { isAvailable: status }),
    });

    let doaSelection = 0;
    switch (flag545) {
      case 0:
        doaSelection = status ? 7 : 6;
        break;
      case 1:
        doaSelection = status ? 5 : 4;
        break;
      case 2:
        doaSelection = status ? 3 : 2;
        break;
    }
    setSubmitting(true);

    fetcher.submit(
      formData,
      {
        method: "put",
        ...(statusType === StatusType.available && {
          action: `/doa-forecast/doa-admin/${params.dispatch}/${params.date}/${regId}/${doaSelection}/stats`
        }),
      });
  }

  const onLoadList = async (list: DoaForecastList, jobId: number, flag545: number) => {
    setFilter(defaultFilter);
    switch (list) {
      case DoaForecastList.available: {
        const currentList = availableLists.find(l => l.jobId === jobId && l.flag545 === flag545);
        if (!currentList) await loadAvailableList(list, jobId, flag545);
        break;
      }
      case DoaForecastList.shortage: {
        setJobInfo({ jobId, flag545 });
        setIsFulfilled(false);
        const currentList = filteredShortageLists.find(l => l.jobId === jobId && l.flag545 === flag545);
        if (jobId != null && flag545 != null && !currentList) {
          await loadShortageList(DoaForecastList.shortage, jobId, flag545, filter.selectedBoardType);
        }
        break;
      }
      // forecast stats already have job required info
      case DoaForecastList.jobs:
      default:
        break;
    }
  }

  const onFilterUpdate = (updatedFilter: IShortageListFilter) => {
    setFilter(prev => prev.selectedBoardType !== updatedFilter.selectedBoardType ?
      ({ ...defaultFilter, selectedBoardType: updatedFilter.selectedBoardType }) : updatedFilter);

    const { jobId, flag545 } = jobInfo;
    if (jobId != null && flag545 != null && updatedFilter.selectedBoardType !== filter.selectedBoardType) {
      loadShortageList(DoaForecastList.shortage, jobId, flag545, updatedFilter.selectedBoardType);
    }
  }

  const onSelectTab = (_event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  }

  return <DoaForecastStats
    isFulfilled={isFulfilled}
    loading={loading}
    submitting={submitting}
    onRefresh={onRefresh}
    onLoadList={onLoadList}
    onFilterUpdate={onFilterUpdate}
    dispatchData={dispatchData}
    availableLists={availableLists}
    shortageLists={filteredShortageLists}
    filter={filter}
    onUpdateCandidateStatuses={onUpdateCandidateStatuses}
    tab={tab}
    onSelectTab={onSelectTab}
  />
}